import React, { type FC } from 'react'
import { Box, Grid, Link, type SxProps, type Theme, Typography } from '@mui/material'

import { isObjectEmpty, objectEntries, type ReadonlyRecord } from '../../../../utils'

import { type CybusPermission, type CybusPermissionOperations, selectIsLoading, selectUsersManagementViewPermission, Translation } from '../../../../domain'

import { useAsyncCallback } from '../../Callback'
import { Copy } from '../../Copy'
import { FormattedList, FormattedTranslation } from '../../Internationalization'
import { useAppState, useAppUsecase, useAppUsecases } from '../../State'
import { useTranslatedOperationPermission } from '../Hooks'
import { EntryInputLabel, GridFormEntry, ViewDialog } from '../Modal'
import { useContextLabel, useContextName } from './Hooks'

const resourceWrapperStyle: SxProps<Theme> = { display: 'flex', '& > *': { my: 'auto', mx: 0 } }
const resourceStyle: SxProps<Theme> = {
    typography: 'body2',
    background: 'grey.100',
    borderRadius: 3,
    color: 'grey.800',
    py: 0.25,
    px: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
}
const copyWrapperStyle: SxProps<Theme> = { display: 'flex' }
const copyStyle: SxProps<Theme> = { ml: 'auto' }

const OperationsTable: FC<{ data: ReadonlyRecord<string, CybusPermissionOperations>, onClick: (name: string) => Promise<void> }> = ({ data, onClick }) => {
    const translate = useTranslatedOperationPermission()
    const isLoading = useAppState(selectIsLoading)
    const [onAsyncClick] = useAsyncCallback((name: string) => onClick(name), [onClick])

    const grouped = objectEntries(data).reduce<ReadonlyRecord<string, string[]>>((r, [name, { read, write }]) => {
        const label = translate({ read, write })
        return { ...r, [label]: [...(r[label] || []), name] }
    }, {})

    return (
        <Grid container alignContent="center" alignItems="center">
            {objectEntries(grouped).map(([label, values], k) => (
                <GridFormEntry
                    key={k}
                    label={
                        <Typography variant="body2">
                            {'- '}
                            {label}
                        </Typography>
                    }
                >
                    <Typography variant="body2">
                        <FormattedList
                            value={isLoading
                                ? values
                                : values.sort().map((name, k) => (
                                    <Link key={k} onClick={() => onAsyncClick(name)}>
                                        {name}
                                    </Link>
                                ))}
                            style="narrow"
                        />
                    </Typography>
                </GridFormEntry>
            ))}
        </Grid>
    )
}

const ViewModalContent: FC<Readonly<{ permission: CybusPermission }>> = ({ permission: info }) => {
    const { editUserUsecase, editRoleUsecase } = useAppUsecases()
    const translateContext = useContextName()
    const translateContextResource = useContextLabel()

    return (
        <Grid container spacing={2} alignContent="center" alignItems="center">
            <GridFormEntry
                label={<EntryInputLabel label={translateContextResource(info.context)} />}
                endAdornment={
                    <Box sx={copyWrapperStyle}>
                        <Copy sx={copyStyle}>{info.resource}</Copy>
                    </Box>
                }
            >
                <Box sx={resourceWrapperStyle}>
                    <Box component={'span'} sx={resourceStyle}>
                        {info.resource}
                    </Box>
                </Box>
            </GridFormEntry>
            <GridFormEntry label={<EntryInputLabel label={Translation.CONTEXT} />}>
                <Typography variant="body2">{translateContext(info.context)}</Typography>
            </GridFormEntry>

            {!isObjectEmpty(info.users) && (
                <>
                    <GridFormEntry label={<EntryInputLabel label={Translation.USER} values={{ count: 2 }} />} />
                    <Grid data-testid="users" size={{ xs: 12 }}>
                        <OperationsTable data={info.users} onClick={(name) => editUserUsecase.load(name)} />
                    </Grid>
                </>
            )}

            {!isObjectEmpty(info.roles) && (
                <>
                    <GridFormEntry label={<EntryInputLabel label={Translation.ROLE} values={{ count: 2 }} />} />
                    <Grid data-testid="roles" size={{ xs: 12 }}>
                        <OperationsTable data={info.roles} onClick={(name) => editRoleUsecase.load(name)} />
                    </Grid>
                </>
            )}
        </Grid>
    )
}

export const PermissionViewModal: FC = () => {
    const viewPermissionUsecase = useAppUsecase('viewPermissionUsecase')
    const permission = useAppState(selectUsersManagementViewPermission)
    const isTransfering = useAppState(selectIsLoading)

    return (
        permission && (
            <ViewDialog
                maxWidth="sm"
                title={<FormattedTranslation id={Translation.PERMISSION} values={{ count: 1 }} />}
                onClose={!isTransfering ? () => viewPermissionUsecase.close() : undefined}
                loading={isTransfering ? true : null}
            >
                <ViewModalContent permission={permission} />
            </ViewDialog>
        )
    )
}
